import React, { useState } from "react";
import {
  Button,
  Group,
  Text,
  Title,
  useMantineColorScheme,
  Container,
  Badge,
  Paper,
  Loader,
  Center,
  Avatar,
  Divider,
  Stack,
  Textarea,
  Card,
  Alert,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconArrowLeft,
  IconCheck,
  IconX,
  IconPlayerPlay,
  IconAlertCircle,
  IconArrowRight,
} from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useTransactionDetailsQuery,
  useSetTransactionApprovalStatusMutation,
  useExecuteTransactionMutation,
  ApprovalStatus,
} from "generated/graphql";
import { format } from "date-fns";
import {
  getTransactionTypeLabel,
  getTransactionTypeColor,
  getApprovalStatusColor,
} from "./transactionUtils";

/**
 * TransactionDetail
 *
 * Detailed view of a transaction with approval controls.
 * Allows league staff to review, approve, or reject transactions.
 */
export default function TransactionDetail() {
  const navigate = useNavigate();
  const { transactionId } = useParams<{ transactionId: string }>();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const [approvalNotes, setApprovalNotes] = useState("");

  const { data, loading, error, refetch } = useTransactionDetailsQuery({
    variables: { transactionId: transactionId! },
    skip: !transactionId,
    fetchPolicy: "cache-and-network",
  });

  const [setApprovalStatus, { loading: approvingLoading }] =
    useSetTransactionApprovalStatusMutation();

  const [executeTransaction, { loading: executingLoading }] =
    useExecuteTransactionMutation();

  const transaction = data?.transaction;

  // Find the pending league staff approval that we can act on
  const pendingLeagueApproval = transaction?.transactionApprovals.find(
    (a) => a.isLeagueStaffApproval && a.approvalStatus === ApprovalStatus.Pending
  );

  // Check if all approvals are complete
  const allApproved = transaction?.transactionApprovals.every(
    (a) => a.approvalStatus === ApprovalStatus.Approved
  );

  async function handleApprove() {
    if (!pendingLeagueApproval) return;

    try {
      await setApprovalStatus({
        variables: {
          input: {
            id: pendingLeagueApproval.id,
            approvalStatus: ApprovalStatus.Approved,
            notes: approvalNotes || undefined,
          },
        },
      });
      notifications.show({
        title: "Success",
        message: "Transaction approved successfully",
        color: "green",
      });
      setApprovalNotes("");
      refetch();
    } catch (err) {
      notifications.show({
        title: "Error",
        message: "Failed to approve transaction",
        color: "red",
      });
    }
  }

  async function handleReject() {
    if (!pendingLeagueApproval) return;

    if (!approvalNotes.trim()) {
      notifications.show({
        title: "Notes Required",
        message: "Please provide a reason for rejecting this transaction",
        color: "yellow",
      });
      return;
    }

    try {
      await setApprovalStatus({
        variables: {
          input: {
            id: pendingLeagueApproval.id,
            approvalStatus: ApprovalStatus.Rejected,
            notes: approvalNotes,
          },
        },
      });
      notifications.show({
        title: "Transaction Rejected",
        message: "Transaction has been rejected",
        color: "orange",
      });
      navigate("/tm-hub/pending");
    } catch (err) {
      notifications.show({
        title: "Error",
        message: "Failed to reject transaction",
        color: "red",
      });
    }
  }

  async function handleExecute() {
    if (!transactionId) return;

    try {
      await executeTransaction({
        variables: {
          transactionId,
        },
      });
      notifications.show({
        title: "Transaction Executed",
        message: "Transaction has been executed and roster changes applied",
        color: "green",
      });
      refetch();
    } catch (err) {
      notifications.show({
        title: "Error",
        message: `Failed to execute transaction. ${err instanceof Error ? err.message : "Unknown error"}`,
        color: "red",
      });
    }
  }

  if (!transactionId) {
    return (
      <Container fluid px="lg" py="lg">
        <Text c="red">Transaction ID is required</Text>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
        <Group justify="flex-start" mb="sm">
          <Button
            variant="filled"
            color="blue"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/tm-hub/pending")}
            size="sm"
          >
            Back to Pending Approvals
          </Button>
        </Group>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (error || !transaction) {
    return (
      <Container fluid px="lg" py="lg">
        <Group justify="flex-start" mb="sm">
          <Button
            variant="filled"
            color="blue"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/tm-hub/pending")}
            size="sm"
          >
            Back to Pending Approvals
          </Button>
        </Group>
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          {error?.message || "Transaction not found"}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/tm-hub/pending")}
          size="sm"
        >
          Back to Pending Approvals
        </Button>
      </Group>

      <div className="space-y-4">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title c={isDark ? "white" : "black"} size="h3" className="sm:text-2xl">
              Transaction Details
            </Title>
            <Text c={isDark ? "gray.4" : "gray.7"} size="sm">
              ID: {transaction.id}
            </Text>
          </div>
          <Badge color={getTransactionTypeColor(transaction.transactionType)} size="lg">
            {getTransactionTypeLabel(transaction.transactionType)}
          </Badge>
        </Group>

        {/* Transaction Info */}
        <Paper withBorder radius="md" p="md">
          <Stack gap="sm">
            <Group gap="xl">
              <div>
                <Text size="xs" c="dimmed">
                  Submitted By
                </Text>
                <Group gap="xs">
                  <Avatar src={transaction.submitter?.avatarUrl} size="sm" radius="xl" />
                  <Text size="sm">{transaction.submitter?.name || "Unknown"}</Text>
                </Group>
              </div>
              <div>
                <Text size="xs" c="dimmed">
                  Date Submitted
                </Text>
                <Text size="sm">
                  {format(new Date(transaction.dateSubmitted), "MMM d, yyyy h:mm a")}
                </Text>
              </div>
              {transaction.dateExecuted && (
                <div>
                  <Text size="xs" c="dimmed">
                    Date Executed
                  </Text>
                  <Text size="sm">
                    {format(new Date(transaction.dateExecuted), "MMM d, yyyy h:mm a")}
                  </Text>
                </div>
              )}
            </Group>

            {/* Franchise Info */}
            {transaction.submittingFranchise && (
              <div>
                <Text size="xs" c="dimmed">
                  Submitting Franchise
                </Text>
                <Group gap="xs">
                  {transaction.submittingFranchise.logoUrl && (
                    <Avatar src={transaction.submittingFranchise.logoUrl} size="sm" radius="sm" />
                  )}
                  <Text size="sm">
                    {transaction.submittingFranchise.prefix
                      ? `[${transaction.submittingFranchise.prefix}] `
                      : ""}
                    {transaction.submittingFranchise.name}
                  </Text>
                </Group>
              </div>
            )}

            {transaction.tradePartnerFranchise && (
              <div>
                <Text size="xs" c="dimmed">
                  Trade Partner
                </Text>
                <Group gap="xs">
                  {transaction.tradePartnerFranchise.logoUrl && (
                    <Avatar src={transaction.tradePartnerFranchise.logoUrl} size="sm" radius="sm" />
                  )}
                  <Text size="sm">
                    {transaction.tradePartnerFranchise.prefix
                      ? `[${transaction.tradePartnerFranchise.prefix}] `
                      : ""}
                    {transaction.tradePartnerFranchise.name}
                  </Text>
                </Group>
              </div>
            )}

            {transaction.notes && (
              <div>
                <Text size="xs" c="dimmed">
                  Notes
                </Text>
                <Text size="sm">{transaction.notes}</Text>
              </div>
            )}
          </Stack>
        </Paper>

        {/* Transaction Items */}
        <Paper withBorder radius="md" p="md">
          <Title order={5} mb="sm">
            Transaction Items
          </Title>
          {transaction.transactionItems.length === 0 ? (
            <Text size="sm" c="dimmed">
              No items in this transaction.
            </Text>
          ) : (
            <Stack gap="sm">
              {transaction.transactionItems.map((item) => (
                <Card key={item.id} withBorder padding="sm">
                  <Group gap="md">
                    {item.player && (
                      <>
                        <Avatar src={item.player.avatarUrl} size="md" radius="xl" />
                        <div>
                          <Text size="sm" fw={500}>
                            {item.player.name}
                          </Text>
                          {item.player.mmr && (
                            <Text size="xs" c="dimmed">
                              MMR: {item.player.mmr}
                            </Text>
                          )}
                        </div>
                      </>
                    )}
                    {item.itemOwner && item.itemReceiver && (
                      <Group gap="xs">
                        <Badge variant="light" color="red">
                          {item.itemOwner.name}
                        </Badge>
                        <IconArrowRight size={16} />
                        <Badge variant="light" color="green">
                          {item.itemReceiver.name}
                        </Badge>
                      </Group>
                    )}
                    {item.itemOwner && !item.itemReceiver && (
                      <Badge variant="light" color="gray">
                        From: {item.itemOwner.name}
                      </Badge>
                    )}
                    {!item.itemOwner && item.itemReceiver && (
                      <Badge variant="light" color="gray">
                        To: {item.itemReceiver.name}
                      </Badge>
                    )}
                  </Group>
                </Card>
              ))}
            </Stack>
          )}
        </Paper>

        {/* Approvals */}
        <Paper withBorder radius="md" p="md">
          <Title order={5} mb="sm">
            Approvals
          </Title>
          <Stack gap="sm">
            {transaction.transactionApprovals.map((approval) => (
              <Card key={approval.id} withBorder padding="sm">
                <Group justify="space-between">
                  <div>
                    <Group gap="xs">
                      <Badge
                        color={getApprovalStatusColor(approval.approvalStatus)}
                        variant="light"
                      >
                        {approval.approvalStatus}
                      </Badge>
                      {approval.isLeagueStaffApproval && (
                        <Badge variant="outline" color="blue" size="sm">
                          League Staff
                        </Badge>
                      )}
                      {approval.isFranchiseStaffApproval && (
                        <Badge variant="outline" color="grape" size="sm">
                          Franchise Staff
                        </Badge>
                      )}
                    </Group>
                    {approval.approvingFranchise && (
                      <Text size="xs" c="dimmed" mt={4}>
                        {approval.approvingFranchise.prefix
                          ? `[${approval.approvingFranchise.prefix}] `
                          : ""}
                        {approval.approvingFranchise.name}
                      </Text>
                    )}
                  </div>
                  <div className="text-right">
                    {approval.approver && (
                      <Text size="sm">Approved by: {approval.approver.name}</Text>
                    )}
                    {approval.approvalDate && (
                      <Text size="xs" c="dimmed">
                        {format(new Date(approval.approvalDate), "MMM d, yyyy h:mm a")}
                      </Text>
                    )}
                  </div>
                </Group>
                {approval.notes && (
                  <Text size="sm" mt="xs" c="dimmed">
                    {approval.notes}
                  </Text>
                )}
              </Card>
            ))}
          </Stack>
        </Paper>

        {/* Action Buttons */}
        {pendingLeagueApproval && (
          <Paper withBorder radius="md" p="md">
            <Title order={5} mb="sm">
              Take Action
            </Title>
            <Stack gap="md">
              <Textarea
                label="Approval Notes (optional)"
                placeholder="Add any notes about this approval decision..."
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.currentTarget.value)}
                minRows={2}
              />
              <Group gap="md">
                <Button
                  color="green"
                  leftSection={<IconCheck size={16} />}
                  onClick={handleApprove}
                  loading={approvingLoading}
                >
                  Approve
                </Button>
                <Button
                  color="red"
                  variant="outline"
                  leftSection={<IconX size={16} />}
                  onClick={handleReject}
                  loading={approvingLoading}
                >
                  Reject
                </Button>
              </Group>
            </Stack>
          </Paper>
        )}

        {/* Execute Button - enabled when all approvals complete */}
        {allApproved && !transaction.dateExecuted && (
          <Alert icon={<IconPlayerPlay size={16} />} title="Ready for Execution" color="blue">
            <Text size="sm">
              All approvals are complete. Transaction can be executed to apply roster changes.
            </Text>
            <Button
              mt="sm"
              color="blue"
              leftSection={<IconPlayerPlay size={16} />}
              onClick={handleExecute}
              loading={executingLoading}
            >
              Execute Transaction
            </Button>
          </Alert>
        )}

        {transaction.dateExecuted && (
          <Alert icon={<IconCheck size={16} />} title="Executed" color="green">
            This transaction has been executed on{" "}
            {format(new Date(transaction.dateExecuted), "MMM d, yyyy h:mm a")}.
          </Alert>
        )}
      </div>
    </Container>
  );
}
